import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  BrowserRouter, Navigate, Route, Routes,
} from 'react-router-dom';
import { LicenseInfo } from '@mui/x-license-pro';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-au';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { RootState, store } from './app/store';
import Middle from './containers/Middle';
import { fetchMe } from './features/me/me';
import { HOME } from './home';
import './index.css';
import theme from './theme';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import reportWebVitals from './reportWebVitals';
import ProtectedRoute from './components/ProtectedRoute';
import Plans from './components/Plans';
import PlanCard from './components/PlanCard';
import Disclaimers from './components/Disclaimers';
import Campaigns from './components/campaigns/Campaigns';
import Routers from './components/Routers';
import RouterCard from './components/RouterCard';
import Terms from './components/Terms';
import Logout from './components/Logout';
import Referrals from './components/Referrals';
import ReferralReportPage from './components/ReferralReportPage';
import ReferAFriendDemo from './components/ReferAFriendBackendDemo';
import CRNReconciliationDemo from './components/CRNReconciliationDemo';
import CommissionSales from './components/CommissionSales';
import AddSalesPerson from './components/AddSalesPerson';
import ActiveReservations from './components/ActiveReservations';
import { PostSignUpReferral } from './components/PostSignUpReferral';
import AdminCouponsPage from './coupons/Coupons';
import { AccountsServicesPage } from './accounts/Accounts';
import LocationPage from './accounts/LocationPage';
import SignupStatsPage from './components/SignupStatsPage';
import BuildingEditor from './components/BuildingEditor';
import CfOrders from './components/CfOrders';
import BuildingEditorDetail from './components/BuildingEditorDetail';

const container = document.getElementById('root')!;
const root = createRoot(container);

LicenseInfo.setLicenseKey('126d91309f82b23c1e0ba1c960f4b653Tz02MDg4NSxFPTE3MDkxNTY3NTU5NzAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=');

export interface Decoded {
  name: string;
  picture: string;
}

const Router = () => {
  const meState = useAppSelector((state: RootState) => state.me);

  const dispatch = useAppDispatch();
  useEffect(
    () => {
      dispatch(fetchMe(meState.value.token));
    },
    [meState.value.authenticate],
  );

  if (meState.status === 'loading') {
    return (
      <Middle
        outlet={
          <CircularProgress size="10rem" color="primary" />
        }
      />
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={HOME} replace />}
        />
        <Route
          path="dashboard"
          element={(
            <ProtectedRoute
              auth={meState.value.authenticate === 'success'}
              authenticationPath="/login"
              outlet={<Dashboard />}
            />
          )}
        >
          <Route
            path="plans"
            element={<Plans />}
          />
          <Route
            path="plans/new"
            element={<PlanCard />}
          />
          <Route
            path="plans/:planId"
            element={<PlanCard />}
          />
          <Route
            path="disclaimers"
            element={<Disclaimers />}
          />
          <Route
            path="campaigns"
            element={<Campaigns />}
          />
          <Route
            path="coupons"
            element={<AdminCouponsPage />}
          />
          <Route
            path="routers"
            element={<Routers />}
          />
          <Route
            path="routers/new"
            element={<RouterCard />}
          />
          <Route
            path="routers/:routerId"
            element={<RouterCard />}
          />
          <Route
            path="terms"
            element={<Terms />}
          />
          <Route
            path="referrals"
            element={<Referrals />}
          />
          <Route
            path="accounts"
            element={<AccountsServicesPage />}
          />
          <Route
            path="accounts/location/:locationId"
            element={<LocationPage />}
          />
          <Route
            path="signup-stats"
            element={<SignupStatsPage />}
          />
          <Route
            path="active_reservations"
            element={<ActiveReservations />}
          />
          <Route
            path="commission_sales"
            element={<CommissionSales />}
          />
          <Route
            path="commission_sales/referral_report/:referralCode"
            element={<ReferralReportPage />}
          />
          <Route
            path="commission_sales/add_sales_person"
            element={<AddSalesPerson />}
          />
          <Route
            path="referrals/referral_report/:referralCode"
            element={<ReferralReportPage />}
          />
          <Route
            path="referrals/post_signup/:referralCode"
            element={<PostSignUpReferral />}
          />
          <Route
            path="referrals/demo"
            element={<ReferAFriendDemo />}
          />
          <Route
            path="reconciliation/crn"
            element={<CRNReconciliationDemo />}
          />
          <Route
            path="cf-orders"
            element={<CfOrders />}
          />
          <Route
            path="building-editor"
            element={<BuildingEditor />}
          />
          <Route
            path="building-editor/:buildingId"
            element={<BuildingEditorDetail />}
          />
          {/* bookmarks from when this was the "Legacy Provisioning Portal" */}
          <Route
            path="legacy-provisioning/*"
            element={<Navigate to="/dashboard/building-editor" replace />}
          />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route
          path="logout"
          element={<Logout />}
        />
      </Routes>
    </BrowserRouter>
  );
};

root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
        <Provider store={store}>
          <Router />
        </Provider>
      </LocalizationProvider>
    </ThemeProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
